import { useCallback, useMemo } from "react";
import { AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { RiskLevelCard } from "../../molecules/RiskLevelCard";
import { RiskFactorPointField } from "../../molecules/RiskFactorPointField";
import type { ConfigurationState } from "../../../types/configuration";

interface RiskFactorScoreTabProps {
  config: ConfigurationState;
  onConfigChange: (config: ConfigurationState) => void;
}

const RISK_LEVEL_STYLES: Record<string, { colorClass: string; borderColorClass: string; bgClass: string; icon: React.ReactNode }> = {
  high: {
    colorClass: "text-destructive",
    borderColorClass: "border-destructive/20",
    bgClass: "bg-destructive/5",
    icon: <AlertTriangle className="h-5 w-5" />,
  },
  medium: {
    colorClass: "text-warning",
    borderColorClass: "border-warning/20",
    bgClass: "bg-warning/5",
    icon: <AlertCircle className="h-5 w-5" />,
  },
  low: {
    colorClass: "text-success",
    borderColorClass: "border-success/20",
    bgClass: "bg-success/5",
    icon: <CheckCircle2 className="h-5 w-5" />,
  },
};

/** Maps backend disabled_risk_factors IDs to the kebab-case IDs used in riskFactorPoints. */
const BACKEND_ID_TO_CONFIG_ID: Record<string, string> = {
  indwelling_catheter: "indwelling-catheter",
  receiving_antipsychotic: "receiving-antipsychotic",
  admitted_less_30_days: "admitted-lt-30-days",
  vital_changes: "vital-changes",
  progress_notes_concerns: "progress-notes",
  high_risk_medications: "high-risk-medications",
  low_risk_medications: "low-risk-medications",
  infections: "infections",
  previous_falls: "previous-falls",
  long_term_infections: "long-term-infections",
  adl_increase: "adl-increase",
  locomotion_decline: "locomotion-decline",
};

export function RiskFactorScoreTab({ config, onConfigChange }: RiskFactorScoreTabProps) {
  const disabledConfigIds = useMemo(() => {
    const ids = new Set<string>();
    for (const backendId of config.disabledRiskFactors) {
      const configId = BACKEND_ID_TO_CONFIG_ID[backendId];
      if (configId) ids.add(configId);
    }
    return ids;
  }, [config.disabledRiskFactors]);

  const handleThresholdChange = useCallback(
    (level: string, value: number) => {
      onConfigChange({
        ...config,
        riskLevelThresholds: config.riskLevelThresholds.map((t) =>
          t.level === level ? { ...t, value } : t
        ),
      });
    },
    [config, onConfigChange]
  );

  const handleFactorChange = useCallback(
    (factorId: string, points: number) => {
      onConfigChange({
        ...config,
        riskFactorPoints: config.riskFactorPoints.map((f) =>
          f.id === factorId ? { ...f, points } : f
        ),
      });
    },
    [config, onConfigChange]
  );

  const enabledFactors = useMemo(
    () => config.riskFactorPoints.filter((f) => !disabledConfigIds.has(f.id)),
    [config.riskFactorPoints, disabledConfigIds]
  );

  const primaryFactors = useMemo(
    () => enabledFactors.filter((f) => f.category === "primary"),
    [enabledFactors]
  );

  const newInsightsFactors = useMemo(
    () => enabledFactors.filter((f) => f.category === "new-insights"),
    [enabledFactors]
  );

  return (
    <div className="space-y-8 p-6">
      {/* Risk Level Definition */}
      <div className="border border-border/60 rounded-lg overflow-hidden bg-card">
        <div className="px-6 py-4 bg-muted/30 border-b border-border/60">
          <h3 className="text-lg font-bold text-foreground">Risk Level Definition</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Define the total point thresholds for each risk level classification
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-3 gap-4">
            {config.riskLevelThresholds.map((threshold) => {
              const style = RISK_LEVEL_STYLES[threshold.level];
              return (
                <RiskLevelCard
                  key={threshold.level}
                  icon={style.icon}
                  label={threshold.label}
                  fieldLabel={threshold.fieldLabel}
                  value={threshold.value}
                  onChange={(v) => handleThresholdChange(threshold.level, v)}
                  description={threshold.description}
                  colorClass={style.colorClass}
                  borderColorClass={style.borderColorClass}
                  bgClass={style.bgClass}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Risk Factor Points Configuration */}
      <div className="border border-border/60 rounded-lg overflow-hidden bg-card">
        <div className="px-6 py-4 bg-muted/30 border-b border-border/60">
          <h3 className="text-lg font-bold text-foreground">Risk Factor Points Configuration</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Set point values for each risk factor (0-10 points)
          </p>
        </div>
        <div className="p-6 space-y-6">
          {/* Primary Risk Factors */}
          <div className="space-y-4">
            <h4 className="font-bold text-foreground border-b border-border/60 pb-2">
              Primary Risk Factors
            </h4>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {primaryFactors.map((factor) => (
                <RiskFactorPointField
                  key={factor.id}
                  label={factor.label}
                  description={factor.description}
                  points={factor.points}
                  onChange={(v) => handleFactorChange(factor.id, v)}
                />
              ))}
            </div>
          </div>

          {/* New Insights */}
          <div className="space-y-4">
            <h4 className="font-bold text-foreground border-b border-border/60 pb-2">
              New Insights
            </h4>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {newInsightsFactors.map((factor) => (
                <RiskFactorPointField
                  key={factor.id}
                  label={factor.label}
                  description={factor.description}
                  points={factor.points}
                  onChange={(v) => handleFactorChange(factor.id, v)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
